"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useMemo,
  memo,
} from "react";
import { Session, User } from "@supabase/supabase-js";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signInWithGoogle: () => Promise<{ error?: string } | void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AuthProvider = memo(function AuthProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const isAdminEmail = useMemo(
    () => (email: string | undefined) => {
      if (!email) return false;
      const adminEmails = import.meta.env.VITE_ADMIN_EMAILS?.split(",") || [];
      return adminEmails.includes(email.toLowerCase());
    },
    []
  );

  useEffect(() => {
    let mounted = true;

    const getSession = async () => {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (!mounted) return;

      if (error || !session) {
        setSession(null);
        setUser(null);
        setIsLoading(false);
        navigate("/login");
        return;
      }

      if (!isAdminEmail(session.user.email)) {
        await supabase.auth.signOut();
        setSession(null);
        setUser(null);
        setIsLoading(false);
        navigate("/login");
        return;
      }

      setSession(session);
      setUser(session?.user || null);
      setIsLoading(false);
    };

    getSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!mounted) return;

      if (!session) {
        setSession(null);
        setUser(null);
        setIsLoading(false);
        navigate("/login");
        return;
      }

      if (!isAdminEmail(session.user.email)) {
        await supabase.auth.signOut();
        setSession(null);
        setUser(null);
        setIsLoading(false);
        navigate("/login");
        return;
      }

      setSession(session);
      setUser(session?.user || null);
      setIsLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate, isAdminEmail]);

  const signInWithGoogle = async (): Promise<{ error?: string } | void> => {
    try {
      console.log("Starting Google sign in...");
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: "offline",
            prompt: "consent",
          },
        },
      });

      if (error) {
        console.error("OAuth error:", error);
        return { error: error.message };
      }
    } catch (error) {
      console.error("Unexpected error during sign in:", error);
      return { error: "Unexpected error during sign in" };
    }
  };

  const signOut = async () => {
    try {
      // Clear state first to ensure UI updates immediately
      setSession(null);
      setUser(null);

      // Clear all relevant cookies
      document.cookie =
        "sb-access-token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT";
      document.cookie =
        "sb-refresh-token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT";
      document.cookie =
        "supabase-auth-token=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT";

      // Attempt to sign out of Supabase (but don't block on error)
      const { error } = await supabase.auth.signOut();

      if (error) {
        console.error("Error signing out:", error.message);
        // Continue with navigation even if Supabase signOut fails
      }

      // Force clear localStorage in case any Supabase tokens remain
      localStorage.removeItem("sb-auth-token");
      localStorage.removeItem("supabase.auth.token");

      // Navigate to login
      navigate("/login");
    } catch (error) {
      console.error("Unexpected error during sign out:", error);
      // Still navigate to login in case of error
      navigate("/login");
    }
  };

  const contextValue = useMemo(
    () => ({
      user,
      session,
      isLoading,
      signInWithGoogle,
      signOut,
    }),
    [user, session, isLoading]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
});

const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export { AuthProvider, useAuth };
